/**
 * Custom hook for fetching wallet balance data
 * @module hooks/use-wallet-balance
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getWalletBalance } from '@/lib/api/wallet';
import { getCached, setCached, isFresh } from '@/lib/api/cache';
import type { WalletBalance } from '@/types/wallet';

interface UseWalletBalanceReturn {
  data: WalletBalance | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useWalletBalance(): UseWalletBalanceReturn {
  const [data, setData] = useState<WalletBalance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchWalletBalance = useCallback(async () => {
    const cacheKey = 'wallet-balance';
    try {
      // Stale-while-revalidate: serve cached data immediately if present
      const cached = getCached<WalletBalance>(cacheKey);
      if (cached) setData(cached);

      // Skip network fetch if data is still fresh
      if (isFresh(cacheKey)) {
        if (mountedRef.current) setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      const response = await getWalletBalance();
      if (!mountedRef.current) return;
      if (response.success && response.data) {
        setCached(cacheKey, response.data, 60_000);
        setData(response.data);
      } else {
        setError(response.message || 'Failed to load wallet balance');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWalletBalance();
  }, [fetchWalletBalance]);

  return { data, loading, error, refetch: fetchWalletBalance };
}
